/**
 * Pure helpers for ActivityLog — kept outside the React module so they're
 * trivially unit-testable without a DOM renderer.
 */
import type { DeepActivityEvent } from "@/stores/ai-explorer-store"
import type { TFunction } from "i18next"

/**
 * Mirrors the backend truncation marker defined in
 * ``app/backend/approaches/explorerdeep/streaming.py``. When the backend
 * caps a subagent's buffered LLM output at ``_SUBAGENT_OUTPUT_CAP`` it
 * appends this suffix; we detect it here so the UI can render a
 * "report truncated" note and strip the marker from the displayed body.
 */
export const SUBAGENT_OUTPUT_TRUNCATED_SUFFIX = "\n\n…[truncated]"

export interface SubagentReport {
  /** Display body with the truncation suffix stripped. */
  text: string
  /** Whether the backend capped the report at SUBAGENT_OUTPUT_CAP. */
  truncated: boolean
}

/**
 * Extract the subagent report text attached to a ``task`` ``tool_end``
 * event by ``streaming.py`` (Track F). Returns ``null`` when:
 *   - the event is missing,
 *   - the tool is not ``task`` (only task tool_end carries a report),
 *   - ``result_summary.output_text`` is absent, non-string, or empty.
 *
 * Non-string ``output_text`` is ignored defensively — older clients may
 * see payloads from backends that haven't shipped this field yet.
 */
export function pickSubagentReport(
  event: DeepActivityEvent | null | undefined
): SubagentReport | null {
  if (!event || event.event !== "tool_end" || event.tool !== "task") return null
  const raw = event.result_summary?.output_text
  if (typeof raw !== "string" || raw.length === 0) return null
  if (raw.endsWith(SUBAGENT_OUTPUT_TRUNCATED_SUFFIX)) {
    return {
      text: raw.slice(0, raw.length - SUBAGENT_OUTPUT_TRUNCATED_SUFFIX.length),
      truncated: true,
    }
  }
  return { text: raw, truncated: false }
}

/**
 * Compute elapsed seconds between a paired ``tool_start`` and ``tool_end``
 * event. Returns ``null`` when either side is missing or the timestamps
 * are unparseable — we prefer rendering nothing over a garbage duration.
 *
 * Subtracting two ISO-8601 strings via ``Date`` is plenty for the
 * millisecond-precision we actually have, and matches what the backend
 * emits via ``_now_iso()``.
 */
export function computeElapsedSeconds(
  start: { timestamp?: string | null } | null | undefined,
  end: { timestamp?: string | null } | null | undefined
): number | null {
  if (!start?.timestamp || !end?.timestamp) return null
  const startMs = Date.parse(start.timestamp)
  const endMs = Date.parse(end.timestamp)
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null
  const delta = endMs - startMs
  if (delta < 0) return null
  return delta / 1000
}

/**
 * Human-readable duration badge. Mirrors the conventions already used
 * by ``TodoList``: ``<1s`` for sub-second work, whole seconds up to a
 * minute, ``Mm Ss`` beyond that. We keep it compact so the activity log
 * rows don't wrap just to fit the badge next to the timestamp.
 *
 * ``null`` input returns ``null`` so callers can conditionally render.
 */
export function formatElapsedBadge(seconds: number | null): string | null {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return null
  if (seconds < 1) return "<1s"
  if (seconds < 60) return `${Math.round(seconds)}s`
  const mins = Math.floor(seconds / 60)
  const rem = Math.round(seconds - mins * 60)
  if (rem === 0) return `${mins}m`
  return `${mins}m ${rem}s`
}

// ── Humanized phrases (UI-3) ──────────────────────────────────────────────────

/**
 * Best-effort hostname extraction for ``web_scrape`` rows. The agent
 * sometimes passes raw URLs and sometimes pre-stripped hostnames; we
 * never want to render the full URL because rows have to fit one line.
 * Returns null when the input isn't a parseable absolute URL — callers
 * fall back to a generic phrase.
 */
function extractHost(value: unknown): string | null {
  if (typeof value !== "string" || value.length === 0) return null
  try {
    const u = new URL(value)
    return u.host || null
  } catch {
    return null
  }
}

/**
 * Pull a short query / argument summary off an ``input_summary`` payload.
 * The backend writes ``query`` for search tools, ``url`` for scrapers,
 * ``path`` for file ops, and ``description`` for ``task`` dispatches —
 * we cap at ~60 chars so single-line rows stay tidy.
 */
function pickArgPreview(
  inputSummary: Record<string, unknown> | undefined,
  keys: readonly string[],
  max = 60
): string | null {
  if (!inputSummary) return null
  for (const key of keys) {
    const v = inputSummary[key]
    if (typeof v === "string" && v.length > 0) {
      return v.length > max ? v.slice(0, max) + "…" : v
    }
  }
  return null
}

const FILE_PATH_KEYS = [
  "path",
  "file_path",
  "filepath",
  "filename",
  "file",
  "file_name",
  "query",
] as const

/**
 * Translate a tool invocation into a natural-language phrase ("Searching
 * the web for *climate*", "Reading nasa.gov", "Updating todos") for the
 * abstracted activity view (UI-3).
 *
 * The ``t`` parameter is the i18next translator — every phrase routes
 * through ``deepExplorer.tool_phrases.*`` so the Swedish locale can
 * substitute its own copy. Falls back to the raw tool name when no
 * phrase key exists for the tool.
 */
export function humanizeToolCall(
  paired: { tool: string; source: string; start: DeepActivityEvent | null },
  t: TFunction
): string {
  const { tool, source, start } = paired

  // Subagent dispatch — show as "Dispatching <subagent> on *<query>*".
  // Worker dispatch starts are attributed to the worker lane once the backend
  // resolves their dispatch id, so this must not be limited to the main agent.
  if (tool === "task") {
    // The dispatch description is often a paragraph. We render a
    // *short* preview here so the row stays single-line; the full text
    // lives behind the "View brief" expander on the row itself
    // (see ``extractTaskBrief``).
    const desc = pickArgPreview(start?.input_summary, [
      "description",
      "query",
      "task",
      "goal",
      "prompt",
      "instructions",
    ])
    const subagent =
      pickArgPreview(start?.input_summary, ["subagent_type", "subagent", "agent"]) ??
      (source === "deep_explorer" ? "worker" : source || "worker")
    if (!desc) {
      return t("deepExplorer.tool_phrases.task_generic", {
        subagent,
        defaultValue: `Dispatching ${subagent}`,
      })
    }
    return t("deepExplorer.tool_phrases.task", {
      subagent,
      query: desc,
      defaultValue: `Dispatching ${subagent} on "${desc}"`,
    })
  }

  switch (tool) {
    case "web_search": {
      const q = pickArgPreview(start?.input_summary, ["query", "q"])
      if (!q) {
        return t("deepExplorer.tool_phrases.web_search_generic", {
          defaultValue: "Searching the web",
        })
      }
      return t("deepExplorer.tool_phrases.web_search", {
        query: q,
        defaultValue: `Searching the web for "${q}"`,
      })
    }
    case "library_search":
    case "doc_internal_search":
    case "search_training_content": {
      const q = pickArgPreview(start?.input_summary, ["query", "q"])
      if (!q) {
        return t("deepExplorer.tool_phrases.library_search_generic", {
          defaultValue: "Searching the library",
        })
      }
      return t("deepExplorer.tool_phrases.library_search", {
        query: q,
        defaultValue: `Searching the library for "${q}"`,
      })
    }
    case "microsoft_365_search": {
      const q = pickArgPreview(start?.input_summary, ["query", "q"])
      if (!q) {
        return t("deepExplorer.tool_phrases.microsoft_365_search_generic", {
          defaultValue: "Searching Microsoft 365",
        })
      }
      return t("deepExplorer.tool_phrases.microsoft_365_search", {
        query: q,
        defaultValue: `Searching Microsoft 365 for "${q}"`,
      })
    }
    case "file_search": {
      const q = pickArgPreview(start?.input_summary, ["query", "q", "pattern"])
      if (!q) {
        return t("deepExplorer.tool_phrases.file_search_generic", {
          defaultValue: "Searching files",
        })
      }
      return t("deepExplorer.tool_phrases.file_search", {
        query: q,
        defaultValue: `Searching files for "${q}"`,
      })
    }
    case "web_scrape": {
      const url = start?.input_summary?.url
      const host = extractHost(url)
      if (!host) {
        return t("deepExplorer.tool_phrases.web_scrape_generic", {
          defaultValue: "Reading a webpage",
        })
      }
      return t("deepExplorer.tool_phrases.web_scrape", {
        host,
        defaultValue: `Reading ${host}`,
      })
    }
    case "doc_content_reader": {
      const name = pickArgPreview(start?.input_summary, ["title", "name", "path"]) ?? null
      if (!name) {
        return t("deepExplorer.tool_phrases.doc_content_reader_generic", {
          defaultValue: "Reading a document",
        })
      }
      return t("deepExplorer.tool_phrases.doc_content_reader", {
        name,
        defaultValue: `Reading ${name}`,
      })
    }
    case "service_desk":
      return t("deepExplorer.tool_phrases.service_desk", {
        defaultValue: "Querying the service desk",
      })
    case "netbox":
      return t("deepExplorer.tool_phrases.netbox", {
        defaultValue: "Querying NetBox",
      })
    case "x_one":
      return t("deepExplorer.tool_phrases.x_one", {
        defaultValue: "Querying X-One",
      })
    case "doc_creator":
      return t("deepExplorer.tool_phrases.doc_creator", {
        defaultValue: "Drafting a document",
      })
    case "doc_updater":
      return t("deepExplorer.tool_phrases.doc_updater", {
        defaultValue: "Updating a document",
      })
    case "write_file": {
      const path = pickArgPreview(start?.input_summary, FILE_PATH_KEYS)
      if (!path) {
        return t("deepExplorer.tool_phrases.write_file_generic", {
          defaultValue: "Writing a file",
        })
      }
      return t("deepExplorer.tool_phrases.write_file", {
        path,
        defaultValue: `Writing ${path}`,
      })
    }
    case "edit_file": {
      const path = pickArgPreview(start?.input_summary, FILE_PATH_KEYS)
      if (!path) {
        return t("deepExplorer.tool_phrases.edit_file_generic", {
          defaultValue: "Editing a file",
        })
      }
      return t("deepExplorer.tool_phrases.edit_file", {
        path,
        defaultValue: `Editing ${path}`,
      })
    }
    case "read_file": {
      const path = pickArgPreview(start?.input_summary, FILE_PATH_KEYS)
      if (!path) {
        return t("deepExplorer.tool_phrases.read_file_generic", {
          defaultValue: "Reading a file",
        })
      }
      return t("deepExplorer.tool_phrases.read_file", {
        path,
        defaultValue: `Reading ${path}`,
      })
    }
    case "ls":
      return t("deepExplorer.tool_phrases.ls", {
        defaultValue: "Listing files",
      })
    case "write_todos":
      return t("deepExplorer.tool_phrases.write_todos", {
        defaultValue: "Updating todos",
      })
    case "ask_user":
      return t("deepExplorer.tool_phrases.ask_user", {
        defaultValue: "Asking you a question",
      })
    default:
      return tool
  }
}

export {
  classifyArtifactPath,
  diffTodos,
  extractFilePath,
  extractTaskBrief,
  readTodosFromEvent,
  summarizeTodos,
  type HighlightedArtifact,
  type TodoCounts,
  type TodoDiff,
} from "./activity-row-helpers"

export interface CollapsedRun<T extends { tool: string; source: string }> {
  /** First member of the run — used for source/dispatch metadata + headline rendering. */
  first: T
  /** Every paired event in the run, in arrival order. ``length === 1`` for
   *  un-collapsed singletons. */
  members: T[]
}

const COLLAPSIBLE_TOOLS = new Set([
  "web_scrape",
  "web_search",
  "library_search",
  "doc_internal_search",
  "doc_content_reader",
  "file_search",
  "microsoft_365_search",
  "read_file",
])

/**
 * Collapse runs of consecutive same-tool invocations within a single group.
 * For UI-3: 5 web_scrapes in a row → "Read 5 sources" with expand-to-see-which.
 * Tools outside ``COLLAPSIBLE_TOOLS`` (notably ``task``, ``write_todos``,
 * ``ask_user``) never collapse — each carries semantically distinct context
 * worth showing on its own row.
 */
export function collapseConsecutive<T extends { tool: string; source: string }>(
  paired: T[]
): CollapsedRun<T>[] {
  const out: CollapsedRun<T>[] = []
  for (const p of paired) {
    const last = out.at(-1)
    if (
      last &&
      last.first.tool === p.tool &&
      last.first.source === p.source &&
      COLLAPSIBLE_TOOLS.has(p.tool)
    ) {
      last.members.push(p)
    } else {
      out.push({ first: p, members: [p] })
    }
  }
  return out
}
